import type { EdgeData, HalfEdgeData } from "building-types/provenance/halfEdgeDataTypes";

export function polygonEdgeDataNew(): EdgeData[][][] {
  return [];
}

export function halfEdgeDataNew(levelId: string, halfEdge: number | undefined, entityLocation?: boolean): EdgeData {
  return entityLocation === undefined ? { levelId, halfEdge } : { levelId, halfEdge, entityLocation };
}

export function collectDefinedHalfEdges(halfEdges: EdgeData[] | undefined): {
  halfEdgeData: HalfEdgeData[];
  fallbackLevelId: string | undefined;
} {
  const halfEdgeData: HalfEdgeData[] = [];
  let fallbackLevelId: string | undefined;

  for (const halfEdge of halfEdges || []) {
    if (halfEdge === undefined || halfEdge.halfEdge === undefined) {
      fallbackLevelId = halfEdge?.levelId;
      continue;
    }
    halfEdgeData.push(halfEdge);
  }

  // sometimes we have no half edges, but we have a fallback level id
  if (halfEdgeData.length === 0 && fallbackLevelId) {
    return { halfEdgeData: [], fallbackLevelId };
  }

  return { halfEdgeData, fallbackLevelId: undefined };
}

export function polyLine2EdgeDataNewFromHalfEdges(levelId: string, halfEdges: number[]): EdgeData[] {
  return halfEdges?.map((halfEdge) => halfEdgeDataNew(levelId, halfEdge));
}

export function polygonHalfEdgesToPolygonEdgeData(
  levelId: string,
  polygonHalfEdges: (number | undefined)[][][],
): EdgeData[][][] {
  return polygonHalfEdges?.map((halfEdges) => polyLine2EdgeDataNewFromHalfEdgeArray(levelId, halfEdges));
}

export function polyLine2EdgeDataNewFromHalfEdgeArray(
  levelId: string,
  halfEdges: (number | undefined)[][],
): EdgeData[][] {
  return halfEdges?.map((halfEdge) => halfEdge?.map((y) => halfEdgeDataNew(levelId, y)));
}

export function defaultHalfEdgeData(levelId: string | undefined): EdgeData {
  return levelId ? { levelId, halfEdge: undefined } : undefined;
}

export function edgeDataUnique(edgeA: EdgeData[]): EdgeData[] {
  const hashMap: { [key: string]: EdgeData } = {};

  edgeA?.forEach((edge) => {
    hashMap[edgeDataHashNew(edge)] = edge;
  });

  // if more than one entry and any value is undefined, remove that entry. undefined is only needed if there is only one entry.
  if (Object.keys(hashMap).length > 1) {
    Object.keys(hashMap).forEach((key) => {
      if (hashMap[key] === undefined) {
        delete hashMap[key];
      }
    });
  }

  return Object.values(hashMap);
}

export function halfEdgeDataEntryCopy(halfEdge: HalfEdgeData): HalfEdgeData {
  return { halfEdge: halfEdge.halfEdge, levelId: halfEdge.levelId, entityLocation: halfEdge.entityLocation };
}
export function halfEdgeDataCopy(halfEdges: HalfEdgeData[]): HalfEdgeData[] {
  return halfEdges?.map((halfEdge) => halfEdgeDataEntryCopy(halfEdge));
}

export function edgeDataEntryCopy(edge: EdgeData): EdgeData {
  return edge ? { halfEdge: edge.halfEdge, levelId: edge.levelId, entityLocation: edge.entityLocation } : undefined;
}

export function edgeDataCopy(halfEdge: EdgeData[]): EdgeData[] {
  return halfEdge?.map((halfEdge) => edgeDataEntryCopy(halfEdge));
}

export function polyLine2EdgeDataCopy(polyLineEdgeData: EdgeData[][]): EdgeData[][] {
  return polyLineEdgeData?.map((x) => edgeDataCopy(x));
}

export function polygonEdgeDataCopy(polygonEdgeData: EdgeData[][][]): EdgeData[][][] {
  return polygonEdgeData?.map((x) => polyLine2EdgeDataCopy(x));
}

export function arrayEdgeDataUnion(edgeA: EdgeData[], edgeB: EdgeData[]): EdgeData[] {
  const hashMap: { [key: string]: EdgeData } = {};

  edgeA?.forEach((edge) => {
    hashMap[edgeDataHashNew(edge)] = edgeDataEntryCopy(edge);
  });
  edgeB?.forEach((edge) => {
    hashMap[edgeDataHashNew(edge)] = edgeDataEntryCopy(edge);
  });

  // if more than one entry and any value is undefined, remove that entry. undefined is only needed if there is only one entry.
  if (Object.keys(hashMap).length > 1) {
    Object.keys(hashMap).forEach((key) => {
      if (hashMap[key] === undefined) {
        delete hashMap[key];
      }
    });
  }

  return Object.values(hashMap);
}

function edgeDataHashNew(edge: EdgeData): string {
  return edge ? `${edge.levelId}:${edge.halfEdge}:${!!edge.entityLocation}` : "undefined";
}

export function arrayUnionDistinct(
  edgeA: (number | undefined)[],
  edgeB: (number | undefined)[],
): (number | undefined)[] {
  return Array.from(new Set([...(Array.isArray(edgeA) ? edgeA : []), ...(Array.isArray(edgeB) ? edgeB : [])]));
}

export function arrayNumberUnionDistinct(edgeA: number[], edgeB: number[]): number[] {
  return Array.from(new Set([...(Array.isArray(edgeA) ? edgeA : []), ...(Array.isArray(edgeB) ? edgeB : [])]));
}

export function markHalfEdgesAsFromEntityLocation(halfEdges: EdgeData[]): EdgeData[] {
  return halfEdges.map((he) => (he !== undefined ? { ...he, entityLocation: true } : undefined));
}
